<template>
  <BModal
    modal-name="bulkUpdateLeadSources"
    width="550px"
    height="490px"
    :delegate="modalDelegate"
  >
    <BHeightAdjuster>
      <BModalHeader
        center
        class="modal-header"
        :title="$t('leadSource.bulkUpdate.title')"
        @modal-close="handleClose"
      />
      <BModalBody class="modal-body">
        <BLayout
          class="target mb-400"
          justify-space-between
          align-center
        >
          <span class="target-label">{{ $t('leadSource.bulkUpdate.target') }}</span>
          <span class="fs-500 fw-bold">{{ selectedCallTargetIds.length }}{{ $t('general.matter') }}</span>
        </BLayout>
        <BListItem class="mb-300">
          <template #header>
            <div>{{ $t('leadSource.bulkUpdate.modeLabel') }}</div>
          </template>
          <BLayout align-center>
            <BRadio
              v-model="mode"
              label="add"
              class="mr-300"
            >
              {{ $t('leadSource.bulkUpdate.modeAdd') }}
            </BRadio>
            <BRadio
              v-model="mode"
              label="delete"
            >
              {{ $t('leadSource.bulkUpdate.modeDelete') }}
            </BRadio>
          </BLayout>
        </BListItem>
        <BListItem>
          <template #header>
            <div>{{ $t('leadSource.bulkUpdate.selectLabel') }}</div>
          </template>
          <BSelect
            v-model="selectedLeadSourceIds"
            class="lead-source-select"
            :items="leadSourcesCandidates"
            item-text="label"
            item-value="value"
            :placeholder="$t('leadSource.bulkUpdate.selectPlaceholder')"
            multiple
            filterable
          />
        </BListItem>
        <div
          v-if="isOverLimit"
          class="over-limit mt-200"
        >
          {{ $t('general.bulk.overLimit', { limit: BULK_UPDATE_LIMIT }) }}
        </div>
      </BModalBody>
      <BModalFooter>
        <BLayout
          justify-center
          align-center
        >
          <BBtn
            class="mr-600"
            text
            :disabled="isLoading"
            @click="handleClose"
          >
            {{ $t('general.cancel.text') }}
          </BBtn>
          <BBtn
            type="primary"
            :loading="isLoading"
            :disabled="isUpdateDisabled || isLoading"
            @click="submitBulkUpdateLeadSources"
          >
            {{ $t('general.update.text') }}
          </BBtn>
        </BLayout>
      </BModalFooter>
    </BHeightAdjuster>
  </BModal>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { CallTargetApiService } from '@/api/user/resources/call_target';
import { useConfirm } from '@/composable/message-dialog';
import { useVuexLeadSources } from '@/composable/user/leadSource/lead-source';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_MODAL, KEY_OF_TOAST } from '@/injection-keys';
import { key } from '@/store/index';

type TProps = {
  selectedCallTargetIds: number[];
};
type TMode = 'add' | 'delete';

const props = defineProps<TProps>();

const MODAL_NAME = 'bulkUpdateLeadSources';
const BULK_UPDATE_LIMIT = 100;
const i18n = useI18n();
const store = useStore(key);
const modal = inject(KEY_OF_MODAL);
const toast = inject(KEY_OF_TOAST);
const { wait, doActionWithWait } = useWait();
const { confirm } = useConfirm();
const { leadSourcesCandidates, fetchAll } = useVuexLeadSources();
const api = new CallTargetApiService();

const mode = ref<TMode>('add');
const selectedLeadSourceIds = ref<number[]>([]);

onMounted(() => {
  fetchAll();
});

const isOverLimit = computed<boolean>(() => props.selectedCallTargetIds.length > BULK_UPDATE_LIMIT);
const isUpdateDisabled = computed<boolean>(() => selectedLeadSourceIds.value.length === 0 || isOverLimit.value);
const isLoading = computed<boolean>(() => wait.is(['bulkUpdateLeadSourcesWait']));

const modalDelegate = {
  beforeClose: () => {
    mode.value = 'add';
    selectedLeadSourceIds.value = [];
  },
};

const handleClose = () => {
  modal.hide(MODAL_NAME);
};

const selectedLeadSourceNames = computed<string>(() =>
  leadSourcesCandidates.value
    .filter((candidate) => selectedLeadSourceIds.value.includes(candidate.value as number))
    .map((candidate) => `「${candidate.label}」`)
    .join(''),
);

const submitBulkUpdateLeadSources = async () => {
  const count = props.selectedCallTargetIds.length;
  const confirmMessageKey = mode.value === 'add' ? 'leadSource.bulkUpdate.confirmAdd' : 'leadSource.bulkUpdate.confirmDelete';
  const confirmed = await confirm({
    message: i18n.t(confirmMessageKey, { count, sources: selectedLeadSourceNames.value }),
  });
  if (!confirmed) return;

  let succeeded = false;
  await doActionWithWait('bulkUpdateLeadSourcesWait', async () => {
    try {
      const request = {
        bulkLeadSourcesBody: {
          callTargetIds: props.selectedCallTargetIds,
          leadSourceIds: selectedLeadSourceIds.value,
        },
      };
      if (mode.value === 'add') {
        await api.bulkAppendLeadSources({ request });
      } else {
        await api.bulkDeleteLeadSources({ request });
      }
      succeeded = true;
      const doneMessageKey = mode.value === 'add' ? 'leadSource.bulkUpdate.doneAdd' : 'leadSource.bulkUpdate.doneDelete';
      toast.show(i18n.t(doneMessageKey, { count }));
    } catch {
      toast.show({ message: i18n.t('leadSource.bulkUpdate.failed'), type: 'error' });
    }
  });
  if (!succeeded) return;

  handleClose();
  await getCallList();
};

const getCallList = async () => {
  // UX改善のため、データ再読込時に全体ローディングしないようになっている
  toast.show({ message: i18n.t('callList.update.loading'), type: 'info' });
  await doActionWithWait('getCallTargetsBackgroundWait', async () => {
    await store.dispatch('user/getCallTargetsAction');
  });
  toast.show({ message: i18n.t('callList.update.done'), type: 'success' });
};
</script>

<style lang="scss" scoped>
  .modal-body {
    padding: $basespace-600;
  }

  .target {
    background-color: $concrete-light;
    border-radius: $basespace-100;
    padding: $basespace-300 $basespace-400;
  }

  .target-label {
    color: $textcolor-light;
  }

  .lead-source-select {
    width: 100%;
  }

  .over-limit {
    color: $basecolor-error;
    font-size: $fontsize-100;
  }
</style>
